import type { BadgeTone } from '@/components/ui';
import { isApiError } from '@/lib/api';
import {
  CONSENT_PROCEDURES,
  type ConsentTemplateStatus,
  type WordingSection,
} from '@/features/consent-templates/types';

export const STATUS_LABEL: Record<ConsentTemplateStatus, string> = {
  draft: 'Draft',
  approved: 'Approved',
  retired: 'Retired',
};

export const STATUS_TONE: Record<ConsentTemplateStatus, BadgeTone> = {
  draft: 'warning',
  approved: 'success',
  retired: 'neutral',
};

export const procedureName = (code: string) =>
  CONSENT_PROCEDURES.find((p) => p.code === code)?.name ?? code;

export type WordingLang = 'en' | 'ms';

export const LANG_LABEL: Record<WordingLang, string> = {
  en: 'English',
  ms: 'Bahasa Melayu',
};

/** Section headings as the app shows them (prototype; BM from the API examples). */
export const SECTION_HEADING: Record<
  WordingLang,
  Record<WordingSection, string>
> = {
  en: {
    whatHappens: 'What happens',
    risks: 'Risks',
    alternatives: 'Alternatives',
  },
  ms: {
    whatHappens: 'Apa yang berlaku',
    risks: 'Risiko',
    alternatives: 'Alternatif',
  },
};

export const when = (iso: string) =>
  new Date(iso).toLocaleString('en-MY', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

/** Staff wording for the API's codes; the API's own message is shown under it. */
export const CONSENT_ERRORS: Record<string, string> = {
  VERSION_CONFLICT:
    'Someone else changed this template. It has been reloaded: check it and try again.',
  CONSENT_TEMPLATE_NOT_DRAFT:
    'Only a draft can change, and this revision is no longer a draft. It has been reloaded: create a new revision instead.',
  CONSENT_TEMPLATE_RETIRED:
    'This revision is already retired. It has been reloaded.',
  CONSENT_TEMPLATE_NOT_FOUND: 'This template revision no longer exists.',
  CONSENT_TEMPLATE_DRAFT_EXISTS:
    'This template already has a draft. Edit, approve or discard that draft first.',
  CONSENT_TEMPLATE_SERVICE_TAKEN:
    'Another approved template is already linked to this service. Retire it, or remove the service from this draft, then approve.',
  SERVICE_NOT_FOUND:
    'The chosen service is no longer active. Choose another service or none.',
};

/** Codes after which the template on screen is out of date. */
export const RELOAD_CODES = new Set([
  'VERSION_CONFLICT',
  'CONSENT_TEMPLATE_NOT_DRAFT',
  'CONSENT_TEMPLATE_RETIRED',
  'CONSENT_TEMPLATE_NOT_FOUND',
]);

export interface Failure {
  title: string;
  detail?: string;
  requestId?: string;
}

/** Staff wording for an API error, with the API's own message under it. */
export function toFailure(err: unknown): Failure {
  if (!isApiError(err))
    return { title: 'Could not reach the server. Try again.' };
  const known = CONSENT_ERRORS[err.code];
  return {
    title: known ?? err.message,
    detail: known && err.message !== known ? err.message : undefined,
    requestId: err.requestId,
  };
}
